import { useMemo } from 'react'

import { Box, List, ListItem, Typography } from '@mui/material'
import { animated, useSpring } from '@react-spring/web'
import type { Assistant, DayObject } from 'types/graphql'

import AssistantAvatar from 'src/components/Assistant/AssistantAvatar'
import { NativeObjectTypes } from 'src/lib/objects'

import { standardBoxSpring } from '../spring'

const SummaryPropertyByObjectType = {
  [NativeObjectTypes.Organization]: [
    {
      propertyDefinitionId: 'status/currentStatusOneSentence',
      type: 'text',
    },
    {
      propertyDefinitionId: 'status/highLevelSummary',
      type: 'array',
    },
  ],
  [NativeObjectTypes.Opportunity]: [
    {
      propertyDefinitionId: 'currentSituation',
      type: 'text',
    },
  ],
}

const SidebarObjectSummary = ({
  object,
  assistant,
}: {
  object: DayObject
  assistant?: Assistant | null
}) => {
  const [spring] = useSpring(() => standardBoxSpring, [])

  const values = useMemo(() => {
    if (!object?.objectType) {
      return null
    }

    let hasValue = false
    const allValues = SummaryPropertyByObjectType[object.objectType]?.map(
      (property) => {
        const value =
          object?.properties?.standard?.[property.propertyDefinitionId]?.value

        if (value) {
          hasValue = true
        }

        return {
          propertyDefinitionId: property.propertyDefinitionId,
          value,
          type: property.type,
        }
      }
    )

    if (hasValue) {
      return allValues
    }

    return null
  }, [object.objectType, object.properties?.standard])

  return (
    values && (
      <Box className="sidebar-object-summary">
        <Box className="thread-message-avatar">
          <AssistantAvatar assistant={assistant} />
        </Box>
        <animated.div style={spring}>
          <Box className="thread-message initialized summary-message">
            {(values ?? []).map((property) => (
              <Box key={property.propertyDefinitionId}>
                {property.type === 'text' && (
                  <Typography
                    className="sidebar-object-summary-property-text"
                    variant="body1"
                  >
                    {property.value}
                  </Typography>
                )}
                {property.type === 'array' && property.value?.length > 0 && (
                  <List className="sidebar-object-summary-list">
                    {property.value
                      .filter((item) => item != null && item !== '')
                      .map((item, index) => (
                        <ListItem
                          key={`${property.propertyDefinitionId}-${index}`}
                        >
                          <Typography
                            variant="body1"
                            className="sidebar-object-summary-property-text"
                          >
                            {item}
                          </Typography>
                        </ListItem>
                      ))}
                  </List>
                )}
              </Box>
            ))}
          </Box>
        </animated.div>
      </Box>
    )
  )
}

export default SidebarObjectSummary
